import deepmerge from 'deepmerge';

const lineHeightScale = 1.3;

const roundToEventNumber = (value: number) => 2 * Math.round(value / 2);

export const createFontSize = (size: number) => {
  let minHeight = lineHeightScale * size;
  minHeight = roundToEventNumber(minHeight);
  return {
    fontSize: `${size}px`,
    lineHeight: lineHeightScale,
    minHeight: `${minHeight}px`
  };
};

export const typographySizeRamp = {
  touch: {
    s100: createFontSize(12),
    s200: createFontSize(14),
    s300: createFontSize(16),
    s400: createFontSize(18),
    s500: createFontSize(20),
    s600: createFontSize(24),
    s700: createFontSize(32),
    s800: createFontSize(40),
    s900: createFontSize(48),
    s1000: createFontSize(58),
    s1100: createFontSize(68),
  },
  low: {
    s100: createFontSize(11),
    s200: createFontSize(14),
    s300: createFontSize(16),
    s400: createFontSize(18),
    s500: createFontSize(20),
    s600: createFontSize(24),
    s700: createFontSize(32),
    s800: createFontSize(40),
    s900: createFontSize(48),
    s1000: createFontSize(58),
    s1100: createFontSize(68)
  },
  medium: {
    s100: createFontSize(11),
    s200: createFontSize(12),
    s300: createFontSize(14),
    s400: createFontSize(16),
    s500: createFontSize(18),
    s600: createFontSize(22),
    s700: createFontSize(30),
    s800: createFontSize(36),
    s900: createFontSize(44),
    s1000: createFontSize(56),
    s1100: createFontSize(64)
  },
  high: {
    s100: createFontSize(10),
    s200: createFontSize(11),
    s300: createFontSize(12),
    s400: createFontSize(14),
    s500: createFontSize(16),
    s600: createFontSize(20),
    s700: createFontSize(26),
    s800: createFontSize(32),
    s900: createFontSize(40),
    s1000: createFontSize(50),
    s1100: createFontSize(60)
  }
};

export const typographyWeightRamp = {
  w300: {
    fontFamily: 'var(--font-inter)'
  },
  w400: {
    fontFamily: 'var(--font-inter)'
  },
  w600: {
    fontFamily: 'var(--font-inter)'
  },
  w700: {
    fontFamily: 'var(--font-inter)'
  }
};

type TypographyPaletteInput = {
   type?: string;
   grey60?: string;
   grey300?: string;
   grey900?: string;
   white?: string;
   unknown?: string;
   // allow callers to pass precomputed colors used throughout this module
   color1?: string;
   color2?: string;
   color3?: string;
   color4?: string;
 };

 const createPalette = ({
   type,
   grey60,
   grey300,
   grey900,
   white,
   unknown
}: TypographyPaletteInput = {}) => {
  if (type == 'light') {
    return {
      color1: grey900,
      color2: grey300,
      color3: white,
      color4: white,
      unknown
    }
  } else {
    return {
      color1: white,
      color2: grey60,
      color3: grey900,
      color4: white,
      unknown
    }
  }
};

const createAction = ({
  color1
}: TypographyPaletteInput) => {
  const { w600 } = typographyWeightRamp;
  const action = {
    color: color1,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...w600
  };
  return {
    touch: action,
    low: action,
    medium: action,
    high: action
  }
};

const createActionInverted = ({
  color3
}: TypographyPaletteInput) => {
  const actionInverted = {
    color: color3
  };
  return {
    touch: actionInverted,
    low: actionInverted,
    medium: actionInverted,
    high: actionInverted
  }
};

const createCaption = ({
  color2
}: TypographyPaletteInput) => {
  const createCaptionDensity = (density: keyof typeof typographySizeRamp) => {
    const { s100 } = typographySizeRamp[density];
    const { w400 } = typographyWeightRamp;
    return {
      color: color2,
      ...s100,
      ...w400
    }
  };
  return {
    touch: createCaptionDensity('touch'),
    low: createCaptionDensity('low'),
    medium: createCaptionDensity('medium'),
    high: createCaptionDensity('high')
  }
};

const createCaption2 = ({
  color2
}: TypographyPaletteInput) => {
  const caption2 = {
    color: color2
  };
  return {
    touch: caption2,
    low: caption2,
    medium: caption2,
    high: caption2
  }
};

const createParagraph = ({
  color1
}: TypographyPaletteInput) => {
  const paragraph = {
    color: color1
  };
  return {
    touch: paragraph,
    low: paragraph,
    medium: paragraph,
    high: paragraph
  }
};

const createParagraphInverted = ({
  color4
}: TypographyPaletteInput) => {
  const paragraphInverted = {
    color: color4
  };
  return {
    touch: paragraphInverted,
    low: paragraphInverted,
    medium: paragraphInverted,
    high: paragraphInverted
  }
};

const createPrimaryAction = ({
  color4
}: TypographyPaletteInput) => {
  const primaryAction = {
    color: color4
  };
  return {
    touch: primaryAction,
    low: primaryAction,
    medium: primaryAction,
    high: primaryAction
  }
};

const createRegular = ({
  color1
}: TypographyPaletteInput) => {
  const createRegularDensity = (density: keyof typeof typographySizeRamp) => {
    const { s200 } = typographySizeRamp[density];
    const { w400 } = typographyWeightRamp;
    return {
      color: color1,
      letterSpacing: 0,
      ...s200,
      ...w400
    }
  };
  return {
    touch: createRegularDensity('touch'),
    low: createRegularDensity('low'),
    medium: createRegularDensity('medium'),
    high: createRegularDensity('high')
  }
};

const createSelected = ({
  color4
}: TypographyPaletteInput) => {
  const selected = {
    color: color4
  };
  return {
    touch: selected,
    low: selected,
    medium: selected,
    high: selected
  }
};

const createSubtitle = () => {
  const { w600 } = typographyWeightRamp;
  return {
    touch: w600,
    low: w600,
    medium: w600,
    high: w600
  }
};

const createSubtitleInverted = ({
  color3
}: TypographyPaletteInput) => {
  const subtitleInverted = {
    color: color3
  };
  return {
    touch: subtitleInverted,
    low: subtitleInverted,
    medium: subtitleInverted,
    high: subtitleInverted
  }
};

type TypographyOptions = Record<string, Record<string, any>>;
type GetTypography = (opts?: { variant?: string; density?: keyof typeof typographySizeRamp }) => any;

const createGetTypography = (options: TypographyOptions): GetTypography => ({
                                          variant = 'regular',
                                          density = 'medium'
                                        } = {}) => {
  const { hasOwnProperty } = Object.prototype;
  if (
      !hasOwnProperty.call(options, variant)
      || !hasOwnProperty.call(options[variant], density)
  ) {
    return {}
  }
  return options[variant][density]
};

const createGetTypographyWithoutColor = (getTypography: GetTypography) => (getTypographyOptions?: Parameters<GetTypography>[0]) => {
    const getTypographyResult = getTypography(getTypographyOptions) || {};
    const { color: unusedColor, ...colourlessOptions } = getTypographyResult as Record<string, any>;
    return colourlessOptions;
};

export const createTypographyNext = (palette: TypographyPaletteInput, typography: TypographyOptions | ((palette: TypographyPaletteInput) => TypographyOptions) = {}) => {
  const {
    getTypography: otherGetTypography,
    getTypographyDensity: otherGetTypographyDensity,
    ...otherOptions
  } = typeof typography == 'function' ? typography(palette) : typography;
  const typographyPalette = createPalette(palette);

  const regular = createRegular(typographyPalette);
  const action = createAction(typographyPalette);
  const actionInverted = deepmerge(
      action,
      createActionInverted(typographyPalette)
  );
  const caption = deepmerge(regular, createCaption(typographyPalette));
  const caption2 = deepmerge(regular, createCaption2(typographyPalette));
   const paragraph = deepmerge(regular, createParagraph(typographyPalette));
  const paragraphInverted = deepmerge(
      paragraph,
      createParagraphInverted(typographyPalette)
  );
  const subtitle = deepmerge(regular, createSubtitle());
  const subtitleInverted = deepmerge(
      subtitle,
      createSubtitleInverted(typographyPalette)
  );
  const primaryAction = deepmerge(
      action,
      createPrimaryAction(typographyPalette)
  );
  const selected = deepmerge(regular, createSelected(typographyPalette));
  const options = {
    regular,
    action,
    actionInverted,
    caption,
    caption2,
    paragraph,
    paragraphInverted,
    primaryAction,
    selected,
    subtitle,
    subtitleInverted
  };
  const mergedOptions = deepmerge(options, otherOptions, {
    clone: false
  });
  const getTypography = createGetTypography(mergedOptions as TypographyOptions);
  return {
    getTypography: otherGetTypography || getTypography,
    getTypographyDensity:
    otherGetTypographyDensity
    || createGetTypographyWithoutColor(getTypography),
    ...mergedOptions
  }
};

